import { BrandMark, Button, cn, interactionSoundProps } from "@healthos/ui";
import { CaretDown, type Icon as PhosphorIcon, Plus } from "@phosphor-icons/react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  SIDE_NAV_WIDE_BREAKPOINT_PX,
  shouldUseSideNavFlyout,
  useMinWidth,
} from "../lib/use-min-width";
import { ModeBadge } from "./mode-badge";
import { DEFAULT_NAV_ITEMS, type DefaultNavItem, type NavItemGroup } from "./nav-items";

export { shouldUseSideNavFlyout };

/**
 * Splits a flat `DefaultNavItem[]` into the visual groups the SideNav
 * renders with dividers between them. Items without an explicit `group`
 * default to `main`. The groups are returned as readonly arrays so callers
 * can map without copying and the shape is stable across renders.
 *
 * `footer` items render pinned to the bottom of the aside. `main` items
 * render at the top under the "Menu" eyebrow. `secondary` and `shared`
 * sit in the middle, each after its own divider.
 */
export function groupNavItems(
  items: readonly DefaultNavItem[],
): Record<NavItemGroup, readonly DefaultNavItem[]> {
  const main: DefaultNavItem[] = [];
  const secondary: DefaultNavItem[] = [];
  const shared: DefaultNavItem[] = [];
  const utility: DefaultNavItem[] = [];
  const footer: DefaultNavItem[] = [];
  for (const item of items) {
    const group: NavItemGroup = item.group ?? "main";
    if (group === "secondary") secondary.push(item);
    else if (group === "shared") shared.push(item);
    else if (group === "utility") utility.push(item);
    else if (group === "footer") footer.push(item);
    else main.push(item);
  }
  return { main, secondary, shared, utility, footer };
}

export interface SideNavProps {
  items?: DefaultNavItem[];
  brand?: {
    name: string;
    /** Small eyebrow above the name, used to differentiate ops vs patient surfaces. */
    eyebrow?: string;
    /** Marks the shell as running an opt-in mode. */
    mode?: { label: string; hint: string } | undefined;
  };
  /** Eyebrow above the nav list. Defaults to "Menu". */
  navLabel?: string;
  /** Persistent content rendered above the bottom navigation area. */
  footerSlot?: ReactNode;
  /** Opens the member's manual signal logger. Desktop sidebar only. */
  onLogClick?: () => void;
}

/**
 * SideNav, ported verbatim from the 2026-04-29 handoff `.side` block.
 *
 * Hard rules from the handoff CSS:
 *   - 224px wide, transparent bg, mist border-right
 *   - padding `22px 14px 22px 22px`, gap 24px between brand/nav/foot
 *   - Brand uses sans 600 16px navy (NOT serif teal)
 *   - "Menu" eyebrow is gray-400 mono 10px tracking 0.16em
 *   - Inactive nav is gray-600 weight 450 13.5px
 *   - Active nav is a subtle mist tint + navy text + teal-600 icon
 *   - Footer routes remain pinned above the viewport edge
 */
export function SideNav({
  items = DEFAULT_NAV_ITEMS,
  brand,
  navLabel = "Menu",
  footerSlot,
  onLogClick,
}: SideNavProps) {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const sidebarCollapsed = false;
  const [openFlyoutId, setOpenFlyoutId] = useState<string | null>(null);
  const isWideSidebar = useMinWidth(SIDE_NAV_WIDE_BREAKPOINT_PX);
  const iconRailMode = shouldUseSideNavFlyout(sidebarCollapsed, isWideSidebar);
  const groups = groupNavItems(items);
  const hasSecondary = groups.secondary.length > 0;
  const hasShared = groups.shared.length > 0;
  const hasUtility = groups.utility.length > 0;
  const hasFooter = groups.footer.length > 0;

  const currentPath = location.pathname;

  // Radix owns outside-click/Escape dismissal; this just clears our "which
  // item's flyout" tracking when navigation or layout mode makes it stale
  // (e.g. resizing out of rail mode with a flyout open, then back into it).
  // biome-ignore lint/correctness/useExhaustiveDependencies: iconRailMode isn't read in the body, but a mode flip should still reset stale open state
  useEffect(() => {
    setOpenFlyoutId(null);
  }, [currentPath, iconRailMode]);

  // Radius is owned by each row variant below, so a variant reads as one
  // complete treatment rather than an override of the shared class.
  const navRowClassName = cn(
    "side-nav-link relative flex items-center [transition:background-color_180ms_var(--ease-out-expo),color_180ms_var(--ease-out-expo),box-shadow_180ms_var(--ease-out-expo),transform_120ms_var(--ease-out-expo)] active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100",
    iconRailMode ? "h-14 w-14 justify-center" : "h-auto w-full justify-start gap-2.5 px-2.5",
  );

  const navIconClassName = (nested = false, active = false) =>
    cn(sideNavIconSizeClass(iconRailMode, nested), active ? "text-teal-600" : "text-gray-500");

  const renderNavLink = (item: DefaultNavItem, nested = false) => {
    const { id, label, path, Icon, comingSoon, badge, pulse, disableViewTransition } = item;
    return (
      <NavLink
        key={id}
        to={path}
        // Stable anchor for surfaces that point at a nav row. The id is the
        // registry's, not copy, so it survives a relabel.
        data-nav-id={id}
        end={item.end ?? (path === "/" || path === "/operations")}
        viewTransition={!disableViewTransition}
        // The aria-label overrides row content for AT, so the badge count
        // must ride it (same pattern as the profile trigger's unread label);
        // a bare pill span would be silent to screen readers.
        aria-label={badge && badge > 0 ? `${label}, ${badge} unread` : label}
        // Below 2xl the rail is icon-only; a native title guarantees a hover
        // label even where the CSS tooltip is clipped or unreliable.
        title={label}
        className={({ isActive }) =>
          cn(
            navRowClassName,
            iconRailMode ? "rounded-xl" : "rounded-md",
            "text-sm tracking-[-0.005em]",
            iconRailMode ? "" : nested ? "py-1.5 text-sm" : "py-2",
            isActive
              ? nested
                ? "text-navy bg-mist"
                : "bg-white text-navy"
              : "text-gray-600 hover:bg-mist/45",
            pulse ? "side-nav-link--pulse" : "",
          )
        }
        style={({ isActive }) => ({
          fontWeight: isActive ? 500 : 450,
          // WCAG 2.5.8: a 24px minimum pointer target on every nav row. Kept at 24 (not larger) so the rail column still fits a short viewport without overflowing rows onto the footer.
          minHeight: 24,
          minWidth: 24,
          boxShadow:
            isActive && !nested
              ? "0 0 0 1px color-mix(in srgb, var(--color-mist-dark) 40%, transparent), 0 12px 28px -20px color-mix(in srgb, var(--color-navy) 38%, transparent)"
              : undefined,
        })}
      >
        {({ isActive }) => (
          <>
            <span className={sideNavIconSlotClass(iconRailMode, nested)}>
              <Icon
                size={iconRailMode ? 22 : nested ? 14 : 16}
                weight="regular"
                className={navIconClassName(nested, isActive)}
              />
            </span>
            <span className={cn("min-w-0 truncate", iconRailMode ? "hidden" : "inline")}>
              {label}
            </span>
            {badge && badge > 0 ? (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute top-2 right-2 ml-auto rounded-full bg-teal-100 px-1.5 py-px font-mono text-xs font-medium tabular-nums text-teal-700",
                  iconRailMode ? "absolute top-2 right-2" : "static ml-auto",
                )}
              >
                {badge}
              </span>
            ) : comingSoon ? (
              <span
                className={cn(
                  "hidden rounded-full bg-teal-100 px-1.5 py-px font-mono text-xs font-medium tabular-nums text-teal-700",
                  iconRailMode ? "hidden" : "inline",
                )}
              >
                soon
              </span>
            ) : null}
          </>
        )}
      </NavLink>
    );
  };

  const renderFlyoutNavLink = (child: DefaultNavItem) => {
    const { id, label, path, Icon, disableViewTransition } = child;
    return (
      <DropdownMenu.Item key={id} asChild>
        <NavLink
          to={path}
          end={child.end ?? (path === "/" || path === "/operations")}
          viewTransition={!disableViewTransition}
          className={({ isActive }) =>
            cn(
              "flex w-full items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm tracking-[-0.005em] transition-colors",
              isActive ? "bg-mist text-navy" : "text-gray-600 hover:bg-mist/45",
            )
          }
          style={({ isActive }) => ({
            fontWeight: isActive ? 500 : 450,
          })}
        >
          {({ isActive }) => (
            <>
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                <Icon
                  size={16}
                  weight="regular"
                  className={cn("h-4 w-4", isActive ? "text-teal-600" : "text-gray-500")}
                />
              </span>
              <span className="whitespace-nowrap">{label}</span>
            </>
          )}
        </NavLink>
      </DropdownMenu.Item>
    );
  };

  const renderItem = (item: DefaultNavItem) => {
    if (!item.children?.length) return renderNavLink(item);

    const { id, label, Icon, badge, pulse } = item;
    const active = item.children.some((child) => navPathMatches(child, location.pathname));
    const expanded = expandedSections[id] ?? active;
    const panelId = `side-nav-${id}-children`;

    const trigger = (
      <button
        type="button"
        // Groups carry the anchor too: a surface pointing at "community" must
        // find it whether it renders as a row or as a flyout trigger.
        data-nav-id={id}
        aria-expanded={iconRailMode ? undefined : expanded}
        aria-controls={iconRailMode ? undefined : panelId}
        onClick={
          iconRailMode
            ? undefined
            : () =>
                setExpandedSections((current) => ({
                  ...current,
                  [id]: !(current[id] ?? active),
                }))
        }
        // A distinct sidebar-disclosure cue keyed to the current state: "reveal"
        // (sparkle) as the submenu opens its child links, "collapse" (whisper)
        // as it folds away. Gated to the interactive (non-rail) mode where the
        // click actually toggles.
        {...interactionSoundProps(iconRailMode ? "none" : expanded ? "collapse" : "reveal")}
        title={label}
        className={cn(
          navRowClassName,
          iconRailMode ? "rounded-xl" : "rounded-md py-2",
          "text-sm tracking-[-0.005em]",
          active ? "bg-white text-navy" : "text-gray-600 hover:bg-mist/45",
          iconRailMode ? "data-[state=open]:bg-white data-[state=open]:text-navy" : "",
          pulse ? "side-nav-link--pulse" : "",
        )}
        style={{
          fontWeight: active ? 500 : 450,
          boxShadow:
            active && !iconRailMode
              ? "0 0 0 1px color-mix(in srgb, var(--color-mist-dark) 40%, transparent), 0 12px 28px -20px color-mix(in srgb, var(--color-navy) 38%, transparent)"
              : undefined,
        }}
      >
        <span className={sideNavIconSlotClass(iconRailMode)}>
          <Icon
            size={iconRailMode ? 22 : 16}
            weight="regular"
            className={navIconClassName(false, active)}
          />
        </span>
        <span className={cn("truncate text-left", iconRailMode ? "hidden" : "inline")}>
          {label}
        </span>
        {badge && badge > 0 ? (
          <span
            className={cn(
              "rounded-full bg-teal-100 px-1.5 py-px font-mono text-xs font-medium tabular-nums text-teal-700",
              iconRailMode ? "absolute top-2 right-2" : "ml-1.5",
            )}
          >
            {badge}
          </span>
        ) : null}
        <CaretDown
          size={13}
          weight="bold"
          className={cn(
            "shrink-0 text-gray-400 [transition:transform_200ms_var(--ease-out-expo)]",
            iconRailMode ? "hidden" : "ml-auto block",
            expanded ? "rotate-0" : "-rotate-90",
          )}
          aria-hidden="true"
        />
      </button>
    );

    if (iconRailMode) {
      return (
        <DropdownMenu.Root
          key={id}
          open={openFlyoutId === id}
          onOpenChange={(open) => setOpenFlyoutId(open ? id : null)}
        >
          <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
          {/* Portal escapes the scrolling nav column: without it, this
              absolutely-positioned panel was a descendant of the
              overflow-y-auto rail and forced a horizontal (and, via reduced
              content-box height, vertical) scrollbar on the whole sidebar. */}
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              side="right"
              align="start"
              sideOffset={10}
              collisionPadding={8}
              className="z-[90] min-w-[13.5rem] rounded-xl border border-hair bg-white py-2 shadow-[0_14px_40px_color-mix(in_srgb,var(--color-navy)_10%,transparent)] outline-none animate-scale-in"
            >
              <DropdownMenu.Label className="m-0 px-4 pb-2 pt-1 font-mono text-2xs font-semibold uppercase tracking-[0.14em] text-gray-400">
                {label}
              </DropdownMenu.Label>
              <div className="flex flex-col gap-1 px-2">
                {item.children.map((child) => renderFlyoutNavLink(child))}
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      );
    }

    return (
      <div key={id} className="w-full">
        {trigger}
        {expanded ? (
          <div id={panelId} className="mt-1 flex w-full flex-col gap-1 pb-1 pl-4">
            {item.children.map((child) => renderNavLink(child, true))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderItems = (itemsToRender: readonly DefaultNavItem[]) => {
    let previousSection: string | undefined;
    return itemsToRender.map((item) => {
      const section = item.sectionLabel;
      const showSection = section && section !== previousSection;
      previousSection = section;
      return (
        <Fragment key={item.id}>
          {showSection ? (
            <p
              className={cn(
                "px-2.5 pt-3 pb-2 font-mono text-xs uppercase tracking-[0.16em] text-gray-400",
                iconRailMode ? "hidden" : "block",
              )}
            >
              {section}
            </p>
          ) : null}
          {renderItem(item)}
        </Fragment>
      );
    });
  };

  return (
    <div
      data-app-chrome="side-nav"
      data-sidebar-collapsed={sidebarCollapsed ? "true" : "false"}
      data-sidebar-compact={iconRailMode ? "true" : "false"}
      className={cn(
        // Scrolls the whole column on a short viewport (rail mode at ~720px) so rows never spill over the mt-auto footer. The px padding keeps overflow-x clip off the active item's focus ring.
        "hidden border-r border-mist bg-transparent md:sticky md:top-0 md:z-[70] md:flex md:h-screen md:flex-col md:overflow-y-auto",
        iconRailMode
          ? "w-[88px] items-center px-3 py-6"
          : "w-[224px] items-stretch py-[22px] pr-[14px] pl-[22px]",
      )}
      style={{ gap: 24 }}
    >
      <div className={cn("flex w-full flex-col", iconRailMode ? "items-center gap-3" : "gap-6")}>
        <div
          className={cn(
            "flex w-full",
            iconRailMode
              ? "flex-col items-center gap-2 px-0 pt-1"
              : "items-center gap-2.5 px-1.5 pt-1",
          )}
        >
          <BrandMark size="nav" className={iconRailMode ? "" : "-ml-1.5"} />
          {!iconRailMode ? (
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              {brand?.eyebrow ? (
                <span className="font-mono text-2xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                  {brand.eyebrow}
                </span>
              ) : null}
              <span className="font-sans text-lg font-semibold leading-none tracking-[-0.01em] text-navy">
                {brand?.name ?? "HealthOS"}
              </span>
              {brand?.mode ? (
                <ModeBadge
                  label={brand.mode.label}
                  hint={brand.mode.hint}
                  className="mt-1 self-start"
                />
              ) : null}
            </div>
          ) : null}
          {iconRailMode && brand?.mode ? (
            <ModeBadge label={brand.mode.label} hint={brand.mode.hint} compact />
          ) : null}
        </div>
        {onLogClick ? <SideNavLogAction compact={iconRailMode} onClick={onLogClick} /> : null}
      </div>

      <nav
        aria-label="Primary"
        className={cn("flex w-full flex-col", iconRailMode ? "items-center gap-3" : "gap-1")}
      >
        <p
          className={cn(
            "px-2.5 pb-2.5 font-mono text-xs uppercase tracking-[0.16em] text-gray-400",
            iconRailMode ? "hidden" : "block",
          )}
        >
          {navLabel}
        </p>
        {renderItems(groups.main)}
        {hasSecondary ? (
          <>
            <div
              aria-hidden="true"
              className={cn("my-3 h-px w-full bg-mist-dark/60", iconRailMode ? "" : "my-3.5")}
            />
            {renderItems(groups.secondary)}
          </>
        ) : null}
        {hasShared ? (
          <>
            <div
              aria-hidden="true"
              className={cn("my-3 h-px w-full bg-mist-dark/60", iconRailMode ? "" : "my-3.5")}
            />
            {renderItems(groups.shared)}
          </>
        ) : null}
        {hasUtility ? (
          <>
            <div
              aria-hidden="true"
              className={cn("my-3 h-px w-full bg-mist-dark/60", iconRailMode ? "" : "my-3.5")}
            />
            {renderItems(groups.utility)}
          </>
        ) : null}
      </nav>

      <div
        className={cn(
          "mt-auto flex w-full flex-col",
          iconRailMode ? "items-center gap-3" : "gap-1",
        )}
      >
        {footerSlot ? <div className="w-full">{footerSlot}</div> : null}
        {hasFooter ? (
          <>
            <div aria-hidden="true" className="h-px w-full bg-mist-dark/45" />
            {/* Its own landmark: pinned below the primary nav, so it sits outside it in the DOM. */}
            <nav
              aria-label="Secondary"
              className={cn("flex w-full flex-col", iconRailMode ? "items-center gap-3" : "gap-1")}
            >
              {renderItems(groups.footer)}
            </nav>
          </>
        ) : null}
      </div>
    </div>
  );
}

function SideNavLogAction({ compact, onClick }: { compact: boolean; onClick: () => void }) {
  return (
    <Button
      variant="primary"
      size="md"
      soundKind="reveal"
      // Anchor for surfaces that point at it, matching the nav rows.
      data-nav-id="add-to-today"
      aria-label="Add to today"
      title="Add to today"
      onClick={onClick}
      leftIcon={compact ? undefined : <Plus size={16} weight="bold" aria-hidden="true" />}
      className={cn(compact ? "h-14 w-14 px-0" : "w-full")}
    >
      {compact ? <Plus size={22} weight="bold" aria-hidden="true" /> : "Add to today"}
    </Button>
  );
}

function sideNavIconSizeClass(iconRailMode: boolean, nested = false): string {
  if (iconRailMode) return "h-[22px] w-[22px]";
  if (nested) return "h-3.5 w-3.5";
  return "h-4 w-4";
}

function sideNavIconSlotClass(iconRailMode: boolean, nested = false): string {
  const size = iconRailMode ? "h-[22px] w-[22px]" : nested ? "h-4 w-4" : "h-4 w-4";
  return cn("inline-flex shrink-0 items-center justify-center", size);
}

function navPathMatches(item: DefaultNavItem, pathname: string): boolean {
  const end = item.end ?? (item.path === "/" || item.path === "/operations");
  if (end) {
    return pathname === item.path;
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

/**
 * Compact sidebar foot action. Matches nav link geometry on desktop and is
 * icon-only on the collapsed rail.
 */
export function SideNavFootAction({
  label,
  icon: Icon,
  onClick,
  compact = false,
}: {
  label: string;
  icon: PhosphorIcon;
  onClick: () => void;
  /** Icon-only rail geometry (matches main nav rows). */
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      // Native title covers the icon-only rail hover label; see the
      // matching comment on the nav-row NavLink above.
      title={label}
      onClick={onClick}
      className={cn(
        "relative flex w-full items-center [transition:background-color_180ms_var(--ease-out-expo),color_180ms_var(--ease-out-expo)]",
        compact
          ? "h-14 w-14 justify-center rounded-xl"
          : "justify-start gap-2.5 rounded-md px-2 py-1.5",
        "text-sm tracking-[-0.005em] text-gray-600 hover:bg-mist/45 hover:text-gray-800",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500",
      )}
      style={{ fontWeight: 450 }}
    >
      <span className={sideNavIconSlotClass(compact)}>
        <Icon
          size={compact ? 22 : 16}
          weight="regular"
          aria-hidden="true"
          className={cn(sideNavIconSizeClass(compact), "text-gray-500")}
        />
      </span>
      <span className={cn(compact ? "hidden" : "inline")}>{label}</span>
    </button>
  );
}
